import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../services/ApiService.js';
import apiService from '../../axios/apiService';
import { useUserBranchLocations } from '../useUserBranchLocations.js';
import { useUserSelectedBranchLocation } from '../useUserSelectedBranchLocation.js';
import { useUser } from '../useUser.js';
import { useUserClinic } from '../useUserClinic.js';
import { useUserToken } from '../useToken.js';
import { mapUserToClinic, mapUserToLocations } from '../../utils/mapper.js';
import { LOGIN_STEPS } from '../../appContants.js';

/**
 * Handles OTP verification, resend, timer state,
 * and post-login session bootstrapping (user/token/branch setup).
 */
const useOtpVerification = ({ cognitoUser, setCognitoUser, formCredentials, goToStep, notify }) => {
  const navigate = useNavigate();
  const { setUserToken } = useUserToken();
  const { setBranchLocations } = useUserBranchLocations();
  const { setUserClinic } = useUserClinic();
  const { selectBranchLocation } = useUserSelectedBranchLocation();
  const { setUser } = useUser();

  const [timerKey, setTimerKey] = useState(0);
  const [isOtpTimerActive, setIsOtpTimerActive] = useState(false);

  const bumpTimer = () => setTimerKey((prev) => prev + 1);

  const setupSession = async () => {
    const user = await apiService.callme();
    if (!user) return;

    setUser(user);
    const branchLocations = mapUserToLocations(user);
    setBranchLocations(branchLocations);
    setUserClinic(mapUserToClinic(user));

    if (branchLocations.length === 1) {
      selectBranchLocation(branchLocations[0]);
      navigate('user/admin-dashboard');
    } else {
      navigate('/select-branch-location');
    }
  };

  const handleOtpComplete = async (value, trustDevice = false) => {
    if (!value.length) return;

    const user = await ApiService.authOtpSignIn(cognitoUser, value);
    if (user?.signInUserSession) {
      setUserToken(user.signInUserSession.idToken.jwtToken);
      setCognitoUser(user);
      if (trustDevice && formCredentials?.email) {
        await ApiService.rememberDevice(formCredentials.email);
      }
      await setupSession();
    } else {
      notify.error({ message: 'Error', description: 'Invalid verification code!!' });
    }
  };

  const handleOtpResend = async () => {
    const res = await ApiService.logIn(formCredentials);
    if (res) {
      setCognitoUser(res);
      goToStep(LOGIN_STEPS.OTP);
      notify.success({
        message: 'OTP Sent',
        description: 'A new OTP has been sent to your registered email/phone.',
      });
      bumpTimer();
      return true;
    }
    return false;
  };

  const resetOtp = () => {
    setTimerKey(0);
    setIsOtpTimerActive(false);
  };

  return {
    timerKey,
    isOtpTimerActive,
    setIsOtpTimerActive,
    bumpTimer,
    setupSession,
    handleOtpComplete,
    handleOtpResend,
    resetOtp,
  };
};

export default useOtpVerification;
